import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Avatar } from "primereact/avatar";
import { Button, Box, TextField, MenuItem, IconButton } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import { getUserDetails } from "../../Redux/Slice/UserSlice";

const genders = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const countries = [
  { value: "India", label: "India" },
  { value: "USA", label: "USA" },
  { value: "UK", label: "United Kingdom" },
];

const UserDetails = () => {
  const dispatch = useDispatch();
  const { items } = useSelector((state) => state.user);
  const user = items?.data;

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.fullName || "",
        email: user.emailId || "",
        phone: user.mobileNo || "",
        age: user.age || "",
        gender: user.gender || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip: user.zipCode || "",
        country: user.country || "",
      });
    }
  }, [user]);

  if (!user) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "#666" }}>
        <h3>No user is logged in.</h3>
        <p>
          Please <a href="/login">log in</a> or register first.
        </p>
      </Box>
    );
  }

  const handleInputChange = (e, field) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const saveChanges = () => {
    // Optionally dispatch an update API here
    setIsEditing(false);
  };

  const cancelEdit = () => {
    if (user) {
      setFormData({
        name: user.fullName || "",
        email: user.emailId || "",
        phone: user.mobileNo || "",
        age: user.age || "",
        gender: user.gender || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip: user.zipCode || "",
        country: user.country || "",
      });
    }
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        display: "flex",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Card
        style={{
          width: 600,
          padding: 24,
          borderRadius: 16,
          boxShadow: "0 10px 30px rgba(100, 100, 150, 0.1)",
          backgroundColor: "#fff",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            position: "relative",
          }}
        >
          <Avatar
            sx={{ bgcolor: "primary.main", width: 56, height: 56 }}
            variant="circular"
          >
            <PersonIcon fontSize="large" />
          </Avatar>
          {!isEditing ? (
            <Box sx={{ ml: 2, flexGrow: 1 }}>
              <Box sx={{ fontWeight: 700, fontSize: 20 }}>
                {formData.name || "User"}
              </Box>
              {/* <Box sx={{ color: "text.secondary" }}>Patient</Box> */}
            </Box>
          ) : (
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name || ""}
              onChange={(e) => handleInputChange(e, "name")}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
          {!isEditing && (
            <IconButton
              sx={{ position: "absolute", right: 0, top: 0 }}
              onClick={() => setIsEditing(true)}
            >
              <EditIcon color="primary" />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}
        />

        {!isEditing ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <InfoRow label="Email" value={formData.email} />
            <InfoRow label="Phone" value={formData.phone} />
            <InfoRow label="Age" value={formData.age} />
            <InfoRow label="Gender" value={formData.gender} />
            <InfoRow label="Address" value={formData.address} />
            <InfoRow label="City" value={formData.city} />
            <InfoRow label="State" value={formData.state} />
            <InfoRow label="Zip / Postal Code" value={formData.zip} />
            <InfoRow label="Country" value={formData.country} />
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              label="Email"
              value={formData.email || ""}
              onChange={(e) => handleInputChange(e, "email")}
              size="small"
              margin="normal"
            />

            <Box sx={{ display: "flex", gap: 2, my: 1 }}>
              <TextField
                fullWidth
                label="Age"
                value={formData.age || ""}
                onChange={(e) => handleInputChange(e, "age")}
                size="small"
              />
              <TextField
                fullWidth
                select
                label="Gender"
                value={formData.gender || ""}
                onChange={(e) => handleInputChange(e, "gender")}
                size="small"
              >
                {genders.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              fullWidth
              label="Mobile"
              value={formData.phone || ""}
              disabled
              size="small"
              margin="normal"
            />

            <TextField
              fullWidth
              label="Address"
              value={formData.address || ""}
              onChange={(e) => handleInputChange(e, "address")}
              size="small"
              margin="normal"
              multiline
              rows={2}
            />

            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ""}
                onChange={(e) => handleInputChange(e, "city")}
                size="small"
              />
              <TextField
                fullWidth
                label="State"
                value={formData.state || ""}
                onChange={(e) => handleInputChange(e, "state")}
                size="small"
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Zip / Postal Code"
                value={formData.zip || ""}
                onChange={(e) => handleInputChange(e, "zip")}
                size="small"
              />
              <TextField
                fullWidth
                select
                label="Country"
                value={formData.country || ""}
                onChange={(e) => handleInputChange(e, "country")}
                size="small"
              >
                {countries.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              <Button variant="outlined" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button variant="contained" onClick={saveChanges}>
                Save
              </Button>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      bgcolor: "background.paper",
      p: 1.5,
      borderRadius: 1,
      boxShadow: 1,
    }}
  >
    <Box sx={{ fontWeight: 600, color: "text.secondary" }}>{label}</Box>
    <Box sx={{ fontWeight: 700 }}>{value || "N/A"}</Box>
  </Box>
);

export default UserDetails;
